import { selectCities, selectCountries, selectStates } from "../store/general/selectors";
import { cityInit, countryInit, countryStateInit } from "../store/general/constants";
import { City, Country, CountryState, ListItem } from "../store/general/types";
import { InterviewQuestion, PostJobStep } from "../store/workspace/types";
import { DayValue } from "@amir04lm26/react-modern-calendar-date-picker";
import { fromDatePickerFormat } from "../utils/dateHelpers";
import { useDispatch, useSelector } from "../store/store";
import { ChangeEvent, useEffect, useState } from "react";
import { setNotification } from "../store/general/slice";
import { selectUserId } from "../store/auth/selectors";
import { useNavigate } from "react-router-dom";
import {
    setPostJobStep,
    setPlansModalShow,
    setContactAdminModal,
} from "../store/workspace/slice";
import {
    postNewJobThunk,
    getJobDescriptionFromAIThunk,
    getRecruitingInterviewQuestionsThunk,
} from "../store/workspace/thunks";
import {
    selectProfile,
    selectPostJobStep,
    selectPostingNewJob,
    selectSubmittingJobModal,
    selectExtractFileTextErrorModal,
    selectRecruitingInterviewQuestions,
} from "../store/workspace/selectors";

export const usePostJob = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userId = useSelector(selectUserId)
    const profile = useSelector(selectProfile);
    const cities = useSelector(selectCities);
    const states = useSelector(selectStates);
    const step = useSelector(selectPostJobStep);
    const countries = useSelector(selectCountries);
    const posting = useSelector(selectPostingNewJob);
    const [stepCount, setStepCount] = useState(1);
    const [jobTitle, setJobTitle] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [city, setCity] = useState<City>(cityInit);
    const [codingExam, setCodingExam] = useState(true);
    const [salaryAmount, setSalaryAmount] = useState('');
    const [deadline, setDeadline] = useState<DayValue>();
    const [jobDescription, setJobDescription] = useState('');
    const [country, setCountry] = useState<Country>(countryInit);
    const [jobType, setJobType] = useState<ListItem | null>(null);
    const [multipleChoiceExam, setMultipleChoiceExam] = useState(true);
    const [state, setState] = useState<CountryState>(countryStateInit);
    const [workplaceType, setWorkplaceType] = useState<ListItem | null>(null);
    const [fetchingJobDescription, setFetchingJobDescription] = useState(false);
    const [salaryFrequency, setSalaryFrequency] = useState<ListItem | null>(null);
    const [codingExamLanguages, setCodingExamLanguages] = useState<ListItem[]>([]);
    const [multipleChoiceCodingExam, setMultipleChoiceCodingExam] = useState(false);
    const [multipleChoiceExamSkills, setMultipleChoiceExamSkills] = useState<string[]>(['']);
    const [multipleChoiceCodingExamLanguages, setMultipleChoiceCodingExamLanguages] = useState(['']);
    const rawInterviewQuestions = useSelector(selectRecruitingInterviewQuestions);
    const [defaultInterviewQuestions, setDefaultInterviewQuestions] = useState<InterviewQuestion[]>([]);
    const [selectedInterviewQuestions, setSelectedInterviewQuestions] = useState<InterviewQuestion[]>([]);
    const [recruitingInterview, setRecruitingInterview] = useState(true);
    const [resumeMatching, setResumeMatching] = useState(true);
    const extractFileTextErrorModal = useSelector(selectExtractFileTextErrorModal);
    const submittingJobModal = useSelector(selectSubmittingJobModal);

    useEffect(() => {
        dispatch(getRecruitingInterviewQuestionsThunk())
        return () => {
            dispatch(setPostJobStep('JobDetail'));
        }
    }, []);

    // useEffect(() => {
    //     if (profile?.postCounter === 0) {
    //         navigate('/workspace');
    //         if (profile.userRole === 'owner') {
    //             dispatch(setPlansModalShow(true));
    //         } else {
    //             dispatch(setContactAdminModal(true));
    //         }
    //     }
    // }, [profile]);

    useEffect(() => {
        if (!rawInterviewQuestions) return;
        setDefaultInterviewQuestions(rawInterviewQuestions.filter(i => !i.default));
        setSelectedInterviewQuestions(rawInterviewQuestions.filter(i => i.default));
    }, [rawInterviewQuestions]);

    useEffect(() => {
        switch (step) {
            case 'JobDetail':
                setStepCount(1);
                break;
            case 'RecruitingInterview':
                setStepCount(2);
                break;
            case 'MultipleChoiceExamDetail':
                setStepCount(3);
                break;
            case 'CodingExamDetail':
                setStepCount(4);
                break;
            case 'ExamDeadline':
                setStepCount(5);
                break;
            case 'Review':
                setStepCount(6);
                break;
        }
    }, [step]);

    const setStep = (step: PostJobStep) => {
        dispatch(setPostJobStep(step));
    };

    const setCityAndState = (city: City) => {
        setCity(city);
        const state = states.find(state => `${state.id}` === city.state_id);
        state && setState(state);
        setCountry({
            id: 228,
            code: 'US',
            title: 'United States'
        });
    };

    const fileSelect = (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        const newList = [...files, e.target.files[0]];
        setFiles(newList);
    };

    const removeFile = (fileName: string) => {
        setFiles(prev => prev.filter(item => item.name !== fileName))
    };

    const updateSkills = (val: string, indx: number) => {
        const newState = [...multipleChoiceExamSkills];
        newState[indx] = val;
        setMultipleChoiceExamSkills(newState);
    };

    const updateMultipleChoiceCodingLanguages = (val: string, indx: number) => {
        const newState = [...multipleChoiceCodingExamLanguages];
        newState[indx] = val;
        setMultipleChoiceCodingExamLanguages(newState);
    };

    const updateCodingExamLanguages = (item: ListItem, action: 'Add' | 'Remove') => {
        if (action === 'Add') {
            if (codingExamLanguages.length > 3) return;
            setCodingExamLanguages(prev => [...new Set([...prev, item])]);
        } else {
            setCodingExamLanguages(prev => prev.filter(i => i.id !== item.id));
        }
    };

    const getJobDescriptionFromAI = () => {
        const skills = multipleChoiceExamSkills.filter(i => Boolean(i.trim()));
        const multipleLangs = multipleChoiceCodingExamLanguages.filter(i => Boolean(i.trim()));
        const codingLangs = codingExamLanguages.filter(i => i.id !== '-1').map(i => i.id);

        const jobInfo = {
            skills,
            get: true,
            city: city.city,
            state: state.state,
            job_title: jobTitle,
            country: country.title,
            job_type: jobType?.title,
            salary_amount: salaryAmount,
            company: profile?.companyName,
            workplace_type: workplaceType?.title,
            deadline: fromDatePickerFormat(deadline),
            salary_frequency: salaryFrequency?.title,
            programming_languages: [...multipleLangs, ...codingLangs],
        };

        setFetchingJobDescription(true);
        dispatch(getJobDescriptionFromAIThunk(jobInfo))
            .unwrap()
            .then((response: any) => {
                setFetchingJobDescription(false);
                if (response) {
                    const parsed = JSON.parse(response);
                    setJobDescription(parsed?.job_description);
                }
            })
            .catch(() => {
                setFetchingJobDescription(false);
                dispatch(setNotification(['Error on getting information. Try again...', 'error']));
            })
    };

    const submit = () => {
        const skills = multipleChoiceExamSkills.filter(i => Boolean(i.trim()));
        const langs = multipleChoiceCodingExamLanguages.filter(i => Boolean(i.trim()));
        const questions = selectedInterviewQuestions.map((i, indx) => ({ id: indx + 1, title: i.title }));

        const jobInfo = {
            city,
            state,
            files,
            country,
            jobType,
            jobTitle,
            id: userId,
            questions,
            codingExam,
            workplaceType,
            jobDescription,
            resumeMatching,
            multipleChoiceExam,
            recruitingInterview,
            baseSalaryAmount: salaryAmount,
            multipleChoiceExamSkills: skills,
            companyName: profile?.companyName,
            fileNames: files.map(i => i.name),
            baseSalaryFrequency: salaryFrequency,
            recruitingInterviewQuestions: questions,
            multipleChoiceCodingExamLanguages: langs,
            companyLogo: profile?.companyLogo || null,
            multipleChoiceExamDeadline: fromDatePickerFormat(deadline),
            multipleChoiceCodingExam: multipleChoiceExam ? multipleChoiceCodingExam : false,
            codingExamLanguages: codingExamLanguages.filter(i => i.id !== '-1').map(i => i.id),
        };

        dispatch(postNewJobThunk(jobInfo));
    };

    const resetInterviewToDefault = () => {
        if (!rawInterviewQuestions) return;
        setDefaultInterviewQuestions(rawInterviewQuestions.filter(i => !i.default));
        setSelectedInterviewQuestions(rawInterviewQuestions.filter(i => i.default));
    };

    return {
        step,
        files,
        setFiles,
        jobTitle,
        posting,
        country,
        state,
        states,
        jobType,
        cities,
        city,
        submit,
        updateSkills,
        setStep,
        setWorkplaceType,
        setJobTitle,
        setJobDescription,
        setSalaryAmount,
        setJobType,
        setCityAndState,
        fileSelect,
        deadline,
        setDeadline,
        removeFile,
        codingExam,
        setCodingExam,
        stepCount,
        resumeMatching,
        setCity,
        setState,
        countries,
        setCountry,
        salaryAmount,
        workplaceType,
        jobDescription,
        salaryFrequency,
        setResumeMatching,
        setSalaryFrequency,
        multipleChoiceExam,
        recruitingInterview,
        codingExamLanguages,
        setMultipleChoiceExam,
        setRecruitingInterview,
        fetchingJobDescription,
        resetInterviewToDefault,
        getJobDescriptionFromAI,
        multipleChoiceCodingExam,
        multipleChoiceExamSkills,
        updateCodingExamLanguages,
        extractFileTextErrorModal,
        defaultInterviewQuestions,
        selectedInterviewQuestions,
        setMultipleChoiceExamSkills,
        setMultipleChoiceCodingExam,
        setDefaultInterviewQuestions,
        setSelectedInterviewQuestions,
        multipleChoiceCodingExamLanguages,
        updateMultipleChoiceCodingLanguages,
        setMultipleChoiceCodingExamLanguages,
        submittingJobModal,
    };
};